import React, { useState, useEffect, useMemo } from "react"
import { useLocation } from "react-router-dom"
import {
  Alert,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
} from "reactstrap"
import { useQuery } from "@tanstack/react-query"

import storage from "services/storage"
import PendingPaidItemList from "./pending-paid-item-list"
import { authUserKey } from "config/API"
import { Loading } from "components"
import ListHeader from "ui-componets/list-header"
import ListFilterBar from "ui-componets/list-filter-bar"
import { getNoPaidTickets, getReceivableTickets } from "services/tickets"
import { PropTypes } from "prop-types"
import { getDateFilterParams } from "utils/date-filters"
import TicketRetrive from "pages/Sales/ticketRetrive"
import TablePaginationFooter from "components/TablePaginationFooter"
import CashRetreat from "pages/PeriodicTasks/cash-retreat"
import "assets/scss/custom/components/table-interactive.scss"

const PAGE_SIZE = 30

const PendingPaidSales = (props) => {
  const {
    setTicketPaying,
    textSerch,
    numItems,
    newInfor,
    setNewInfor,
    initialActiveTab,
    isCashOperationDayOpen,
  } = props

  const location = useLocation()
  const [searchTxt, setSearchTxt] = useState(new URLSearchParams(location.search).get("search") ?? "")
  const [searchTerm, setSearchTerm] = useState(new URLSearchParams(location.search).get("search") ?? "")
  const [dateFilter, setDateFilter] = useState({ preset: "", startDate: "", endDate: "" })
  const [currentPage, setCurrentPage] = useState(1)
  const [modalTicketOpen, setModalTicketOpen] = useState(false)
  const [ticketId, setTicketId] = useState(null)
  const [tabState, setTabState] = useState({ activeTab: initialActiveTab || "sales" })
  const permissions = storage.get(authUserKey).user_permissions || []

  useEffect(() => {
    setTabState({ activeTab: initialActiveTab || "sales" })
  }, [initialActiveTab])

  const queryFilters = useMemo(
    () => ({
      page: currentPage,
      ...(searchTerm ? { search: searchTerm } : {}),
      ...getDateFilterParams(dateFilter, "date"),
    }),
    [currentPage, dateFilter, searchTerm]
  )

  const queryOptions = {
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 3000),
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    retryOnMount: false,
    refetchInterval: 5000,
    staleTime: 0,
    cacheTime: 60000,
    refetchIntervalInBackground: false,
    keepPreviousData: true,
  }

  const ticketsNoPaid = useQuery(["ticketsNoPaid", queryFilters], () => getNoPaidTickets(queryFilters), queryOptions)
  const receivableTickets = useQuery(
    ["ticketsReceivable", queryFilters],
    () => getReceivableTickets(queryFilters),
    queryOptions
  )

  const { refetch: refetchTicketsNoPaid } = ticketsNoPaid
  const { refetch: refetchReceivableTickets } = receivableTickets

  useEffect(() => {
    textSerch(searchTxt)
    numItems(Number(ticketsNoPaid.data?.count || 0) + Number(receivableTickets.data?.count || 0))
  }, [ticketsNoPaid.data?.count, receivableTickets.data?.count, searchTxt])

  useEffect(() => {
    if (newInfor) {
      refetchTicketsNoPaid()
      refetchReceivableTickets()
      setNewInfor(false)
    }
  }, [newInfor, refetchTicketsNoPaid, refetchReceivableTickets, setNewInfor])

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1)
      setSearchTerm(searchTxt.trim())
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTxt])

  const activeCount = tabState.activeTab === "receivable"
    ? receivableTickets.data?.count || 0
    : ticketsNoPaid.data?.count || 0
  const totalPages = activeCount > 0 ? Math.ceil(activeCount / PAGE_SIZE) : 1
  const styleCenterTable = { textAlign: "center", verticalAlign: "middle", padding: "0.3rem" }

  const handleSearch = (pageParams) => {
    if (pageParams) {
      const newParams = new URLSearchParams(pageParams)
      setCurrentPage(Number(newParams.get("page") || 1))
      return
    }

    setCurrentPage(1)
    setSearchTerm(searchTxt.trim())
  }

  const handleChangeSearch = (event) => {
    setSearchTxt(event.target.value)
  }

  const handleDateFilterChange = (nextDateFilter) => {
    setCurrentPage(1)
    setDateFilter(nextDateFilter)
  }

  const clearListFilters = () => {
    setCurrentPage(1)
    setDateFilter({ preset: "", startDate: "", endDate: "" })
  }

  const handleChangePage = (nextPage) => {
    if (nextPage < 1) return
    if (totalPages > 0 && nextPage > totalPages) return
    setCurrentPage(nextPage)
  }

  const openTicketModal = (id) => {
    setTicketId(id)
    setModalTicketOpen(true)
  }

  const closeTicketModal = () => {
    setModalTicketOpen(false)
  }

  const toggleActiveTab = (tab) => {
    if (tabState.activeTab !== tab) {
      setCurrentPage(1)
      setTabState({ activeTab: tab })
    }
  }

  const renderFilters = () => (
    <ListHeader
      handleOnChangeSearch={handleChangeSearch}
      handleSearch={handleSearch}
      searchTxt={searchTxt}
      filtersContent={
        <ListFilterBar
          dateFilter={dateFilter}
          onDateFilterChange={handleDateFilterChange}
          onClear={clearListFilters}
        />
      }
    />
  )

  const renderTicketTable = (query, itemLabel, showReceivableAmounts = false) => {
    const isLoadingQuery = query.isLoading
    const showLoading = isLoadingQuery && !query.data
    const count = query.data?.count || 0

    if (showLoading) {
      return (
        <div className="my-5 text-center">
          <Loading isLoading={true} size={2} />
        </div>
      )
    }

    if (query.isError && !query.isFetching && !query.data) {
      return (
        <div className="my-5 text-center">
          <h1>Error intenta mas tarde</h1>
        </div>
      )
    }

    return (
      <div className="table-responsive mt-3">
        <Table hover size="sm" className="mb-0 pending-paid-sales-table table-hover-effect table-interactive">
          <thead>
            <tr>
              <th style={styleCenterTable}>Folio</th>
              <th style={styleCenterTable} className="text-cell">Fecha de creacion</th>
              <th style={styleCenterTable} className="text-cell">Cajero</th>
              <th style={styleCenterTable} className="text-cell">Cliente</th>
              {showReceivableAmounts && (
                <>
                  <th style={styleCenterTable} className="money-cell">Total</th>
                  <th style={styleCenterTable} className="money-cell">Descuento</th>
                  <th style={styleCenterTable} className="money-cell">Abonado</th>
                  <th style={styleCenterTable} className="money-cell">Pendiente</th>
                  <th style={styleCenterTable}>Estado</th>
                </>
              )}
              {!showReceivableAmounts && <th style={styleCenterTable} className="money-cell">Total</th>}
              <th style={styleCenterTable}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {query.data?.results.length ? (
              query.data?.results.map((item) => (
                <PendingPaidItemList
                  key={item.id}
                  item={item}
                  setTicketPaying={setTicketPaying}
                  openTicketModal={openTicketModal}
                  isCashOperationDayOpen={isCashOperationDayOpen}
                  showReceivableAmounts={showReceivableAmounts}
                />
              ))
            ) : (
              <tr>
                <td colSpan={showReceivableAmounts ? 10 : 6} className="text-center">
                  No hay datos
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <TablePaginationFooter
          totalCount={count}
          currentPage={currentPage}
          pageSize={PAGE_SIZE}
          itemLabel={itemLabel}
          isLoading={isLoadingQuery}
          isFetching={query.isFetching}
          onChangePage={handleChangePage}
        />
      </div>
    )
  }

  return !permissions.some((e) => e.codename === "view_ticket") ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <Container fluid>
      <Alert color="danger" isOpen={ticketsNoPaid.isError && !ticketsNoPaid.isFetching}>
        {ticketsNoPaid.error?.errors?.map((error) => error?.message) || "Error al cargar los tickets"}
      </Alert>
      <Alert color="danger" isOpen={receivableTickets.isError && !receivableTickets.isFetching}>
        {receivableTickets.error?.errors?.map((error) => error?.message) || "Error al cargar los pagares"}
      </Alert>

      <div className="bg-white">
        <Nav tabs>
          <NavItem>
            <NavLink
              className={`${tabState.activeTab === "sales" && "active"}`}
              onClick={() => toggleActiveTab("sales")}
            >
              Ventas por Cobrar
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`${tabState.activeTab === "receivable" && "active"}`}
              onClick={() => toggleActiveTab("receivable")}
            >
              Pagares
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`${tabState.activeTab === "cash" && "active"}`}
              onClick={() => toggleActiveTab("cash")}
            >
              Caja
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={tabState.activeTab}>
          <TabPane tabId="sales">
            {!isCashOperationDayOpen && (
              <Alert color="warning" className="mx-3 mt-3 mb-0">
                No puedes cobrar ventas hasta abrir el dia. Ve a la pestana <strong>Caja</strong> para abrirlo.
              </Alert>
            )}
            <div style={{ marginTop: "20px", marginBottom: "20px" }}></div>
            {renderFilters()}
            {renderTicketTable(ticketsNoPaid, "ventas")}
          </TabPane>
          <TabPane tabId="receivable">
            {!isCashOperationDayOpen && (
              <Alert color="warning" className="mx-3 mt-3 mb-0">
                No puedes cobrar pagares hasta abrir el dia. Ve a la pestana <strong>Caja</strong> para abrirlo.
              </Alert>
            )}
            <div style={{ marginTop: "20px", marginBottom: "20px" }}></div>
            {renderFilters()}
            {renderTicketTable(receivableTickets, "pagares", true)}
          </TabPane>
          <TabPane tabId="cash">
            <div style={{ marginTop: "20px", marginBottom: "20px" }}></div>
            <CashRetreat />
          </TabPane>
        </TabContent>

        {modalTicketOpen && (
          <Modal
            centered={true}
            style={{ maxWidth: "500px", width: "95%" }}
            backdrop={true}
            isOpen={modalTicketOpen}
            toggle={closeTicketModal}
          >
            <ModalHeader toggle={closeTicketModal}>Detalle de ticket</ModalHeader>
            <ModalBody style={{ padding: "0px" }}>
              <TicketRetrive id={ticketId} isModal={true} />
            </ModalBody>
          </Modal>
        )}
      </div>
    </Container>
  )
}

PendingPaidSales.propTypes = {
  activeTab: PropTypes.object,
  initialActiveTab: PropTypes.string,
  setTicketPaying: PropTypes.func,
  textSerch: PropTypes.func,
  numItems: PropTypes.func,
  newInfor: PropTypes.bool,
  setNewInfor: PropTypes.func,
  isCashOperationDayOpen: PropTypes.bool,
}

export default PendingPaidSales
